import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import type { InformationSystemDetail, DataCategoryAssignment } from '../../services/informationSystemsApi'
import {
  fetchDataCategoryCatalog,
  createDataCategoryAssignment,
  updateDataCategoryAssignment,
  deleteDataCategoryAssignment,
} from '../../services/informationSystemsApi'

interface Props {
  system: InformationSystemDetail
  onChanged: () => Promise<void>
}

type FormState = {
  category: string
  classification: string
  processingPurpose: string
  storageLocation: string
  retentionPeriod: string
  crossBorderTransfer: boolean
  notes: string
}

const emptyForm = (): FormState => ({
  category: '',
  classification: '',
  processingPurpose: '',
  storageLocation: '',
  retentionPeriod: '',
  crossBorderTransfer: false,
  notes: '',
})

export default function InformationSystemCriticalDataSection({ system, onChanged }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [catalog, setCatalog] = React.useState<string[]>([])
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [form, setForm] = React.useState<FormState>(emptyForm)
  const [editingId, setEditingId] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!token) return
    fetchDataCategoryCatalog(token)
      .then((c) => setCatalog(Array.isArray(c) ? c : []))
      .catch(() => setCatalog([]))
  }, [token])

  const categories = system.dataCategories || []

  const startEdit = (dc: DataCategoryAssignment) => {
    setEditingId(dc.id)
    setForm({
      category: dc.category,
      classification: dc.classification || '',
      processingPurpose: dc.processingPurpose || '',
      storageLocation: dc.storageLocation || '',
      retentionPeriod: dc.retentionPeriod || '',
      crossBorderTransfer: dc.crossBorderTransfer,
      notes: dc.notes || '',
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setForm(emptyForm())
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !form.category) return
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        category: form.category,
        classification: form.classification || undefined,
        processingPurpose: form.processingPurpose || undefined,
        storageLocation: form.storageLocation || undefined,
        retentionPeriod: form.retentionPeriod || undefined,
        crossBorderTransfer: form.crossBorderTransfer,
        notes: form.notes || undefined,
      }
      if (editingId) {
        await updateDataCategoryAssignment(token, system.id, editingId, payload, orgId)
      } else {
        await createDataCategoryAssignment(token, system.id, payload, orgId)
      }
      cancelEdit()
      await onChanged()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('assets.dataMap.error.assign', 'Failed to save'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (dataCategoryId: string) => {
    if (!token) return
    setError(null)
    try {
      await deleteDataCategoryAssignment(token, system.id, dataCategoryId, orgId)
      if (editingId === dataCategoryId) cancelEdit()
      await onChanged()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('assets.dataMap.error.remove', 'Failed to remove'))
    }
  }

  return (
    <div className={styles.contextSectionBody}>
      <p className={styles.contextHint}>
        {t(
          'assets.dataMap.relationshipHint',
          'System → data categories assigned for processing and storage context.'
        )}
      </p>
      {error ? <p className={styles.contextError}>{error}</p> : null}
      {categories.length === 0 ? (
        <p className={styles.emptyHint}>{t('assets.dataMap.noCategoriesForSystem', 'No categories.')}</p>
      ) : (
        <ul className={styles.contextCategoryList}>
          {categories.map((dc) => (
            <li key={dc.id} className={styles.contextCategoryItem}>
              <div className={styles.contextCategoryMain}>
                <p className={styles.contextCategoryName}>{dc.category}</p>
                {dc.classification ? <p className={styles.contextCategoryMeta}>{dc.classification}</p> : null}
                {dc.processingPurpose ? (
                  <p className={styles.contextCategoryMeta}>
                    {t('assets.dataMap.processingPurpose', 'Processing purpose')}: {dc.processingPurpose}
                  </p>
                ) : null}
                {dc.storageLocation ? (
                  <p className={styles.contextCategoryMeta}>
                    {t('assets.dataMap.storageLocation', 'Storage')}: {dc.storageLocation}
                  </p>
                ) : null}
                {dc.retentionPeriod ? (
                  <p className={styles.contextCategoryMeta}>
                    {t('assets.dataMap.retentionPeriod', 'Retention')}: {dc.retentionPeriod}
                  </p>
                ) : null}
                {dc.crossBorderTransfer ? (
                  <p className={styles.contextCategoryFlag}>{t('assets.dataMap.crossBorder', 'Cross-border transfer')}</p>
                ) : null}
                {dc.notes ? <p className={styles.contextCategoryMeta}>{dc.notes}</p> : null}
              </div>
              <div className={styles.contextCategoryActions}>
                <button type="button" className={styles.contextLinkBtn} onClick={() => startEdit(dc)}>
                  {t('assets.dataMap.editCriticalData', 'Edit')}
                </button>
                <button type="button" className={styles.contextRemoveBtn} onClick={() => handleRemove(dc.id)}>
                  {t('common.remove', 'Remove')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className={styles.contextAssignBlock}>
        <h4 className={styles.contextAssignTitle}>
          {editingId
            ? t('assets.dataMap.editCriticalData', 'Edit critical data')
            : t('assets.dataMap.addCriticalData', 'Add critical data')}
        </h4>
        <form className={styles.contextForm} onSubmit={handleSubmit}>
          <label className={styles.contextFormField}>
            <span className={styles.contextFormLabel}>{t('assets.dataMap.category', 'Data category')}</span>
            <select
              className={styles.contextSelect}
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              required
              disabled={!!editingId}
            >
              <option value="">{t('assets.dataMap.selectCategory', 'Select category…')}</option>
              {catalog.map((c) => (
                <option key={c} value={c}>
                  {t(`assets.dataCategories.${categoryKey(c)}`, c)}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.contextFormField}>
            <span className={styles.contextFormLabel}>{t('assets.dataMap.classification', 'Classification')}</span>
            <input
              className={styles.contextInput}
              value={form.classification}
              onChange={(e) => setForm((f) => ({ ...f, classification: e.target.value }))}
            />
          </label>
          <label className={styles.contextFormField}>
            <span className={styles.contextFormLabel}>{t('assets.dataMap.processingPurpose', 'Processing purpose')}</span>
            <input
              className={styles.contextInput}
              value={form.processingPurpose}
              onChange={(e) => setForm((f) => ({ ...f, processingPurpose: e.target.value }))}
            />
          </label>
          <label className={styles.contextFormField}>
            <span className={styles.contextFormLabel}>{t('assets.dataMap.storageLocation', 'Storage location')}</span>
            <input
              className={styles.contextInput}
              value={form.storageLocation}
              onChange={(e) => setForm((f) => ({ ...f, storageLocation: e.target.value }))}
            />
          </label>
          <label className={styles.contextFormField}>
            <span className={styles.contextFormLabel}>{t('assets.dataMap.retentionPeriod', 'Retention period')}</span>
            <input
              className={styles.contextInput}
              value={form.retentionPeriod}
              onChange={(e) => setForm((f) => ({ ...f, retentionPeriod: e.target.value }))}
            />
          </label>
          <label className={styles.contextCheckboxRow}>
            <input
              type="checkbox"
              checked={form.crossBorderTransfer}
              onChange={(e) => setForm((f) => ({ ...f, crossBorderTransfer: e.target.checked }))}
            />
            <span>{t('assets.dataMap.crossBorder', 'Cross-border transfer')}</span>
          </label>
          <label className={styles.contextFormField}>
            <span className={styles.contextFormLabel}>{t('assets.dataMap.notes', 'Notes')}</span>
            <input
              className={styles.contextInput}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>
          <div className={styles.contextFormActions}>
            <button type="submit" className={styles.contextPrimaryBtn} disabled={submitting || !form.category}>
              {editingId ? t('common.save', 'Save') : t('assets.dataMap.assign', 'Assign')}
            </button>
            {editingId ? (
              <button type="button" className={styles.contextSecondaryBtn} onClick={cancelEdit}>
                {t('common.cancel', 'Cancel')}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  )
}

function categoryKey(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
}
